import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button, Tooltip, useBadge } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { apiClient, apiQuery } from '../../state';
import { UserAvatar } from '../Users';
import { requestReviewersForChangeRequest } from './requestReviewersForChangeRequest';
import { useAddReviewerPalette } from './useAddReviewerPalette';
import {
    type ChangeRequestPendingRequestedReviewer,
    useChangeRequestReviewers,
} from './useChangeRequestReviewers';

export type ChangeRequestReviewersBadgesProps = {
    changeRequest: api.ChangeRequest;
    space: api.Space;

    /**
     * List of pending requested reviewers, not yet synced with the backend.
     */
    pendingRequestedReviewers?: ChangeRequestPendingRequestedReviewer[];
};

type ReviewerBadgeData = {
    userId: string;
    status: 'approved' | 'changes_requested' | 'requested';
};

/**
 * Display reviewers as horizontal badges matching the Figma design.
 */
export function ChangeRequestReviewersBadges(props: ChangeRequestReviewersBadgesProps) {
    const { changeRequest, space, pendingRequestedReviewers = [] } = props;

    const { reviews, requestedReviewers, existingReviewers, suggestions, isLoading } =
        useChangeRequestReviewers(space, changeRequest, pendingRequestedReviewers);

    // Combine reviews, requested reviewers, and suggestions into a single list for display
    const reviewerBadges = React.useMemo(() => {
        const badges: ReviewerBadgeData[] = [];

        // Add reviews (approved or changes requested)
        reviews.forEach((review) => {
            badges.push({
                userId: review.reviewer.id,
                status:
                    review.status === api.ChangeRequestReviewStatus.Approved
                        ? 'approved'
                        : 'changes_requested',
            });
        });

        // Add requested reviewers (pending)
        requestedReviewers.forEach((reviewer) => {
            if (reviewer.kind === 'user') {
                badges.push({
                    userId: reviewer.user.id,
                    status: 'requested',
                });
            }
        });

        // Add pending requested reviewers (not yet synced)
        pendingRequestedReviewers.forEach((reviewer) => {
            // Only add if not already in the list
            if (!badges.some((badge) => badge.userId === reviewer.userId)) {
                badges.push({
                    userId: reviewer.userId,
                    status: 'requested',
                });
            }
        });

        return badges;
    }, [reviews, requestedReviewers, pendingRequestedReviewers]);

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
                <div className="text-muted text-ui-small">Reviewers</div>
            </div>
            <div className="flex flex-wrap gap-2">
                {isLoading ? (
                    <ReviewerBadgeSkeleton />
                ) : (
                    <>
                        {reviewerBadges.map((badge) => (
                            <React.Suspense key={badge.userId} fallback={<ReviewerBadgeSkeleton />}>
                                <ReviewerBadge
                                    userId={badge.userId}
                                    status={badge.status}
                                    space={space}
                                    changeRequest={changeRequest}
                                />
                            </React.Suspense>
                        ))}
                        <AddReviewerPalette
                            space={space}
                            changeRequest={changeRequest}
                            pendingRequestedReviewers={pendingRequestedReviewers}
                            existingReviewers={existingReviewers}
                            suggestions={suggestions}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

function ReviewerBadgeSkeleton() {
    return <div className="h-6 w-24 animate-pulse rounded-full border border-muted bg-subtle" />;
}

function ReviewerBadge(props: {
    userId: string;
    status: ReviewerBadgeData['status'];
    space: api.Space;
    changeRequest: api.ChangeRequest;
}) {
    const { userId, status, space, changeRequest } = props;

    const { data: user } = useSuspenseQuery(
        apiQuery('getUserById', { userId }, { nullOnError: true })
    );

    const { badgeProps } = useBadge({
        kind: 'secondary',
        size: 'small',
        className: 'px-1',
    });

    if (!user) {
        return null;
    }

    const { icon: statusIcon, tooltip: statusTooltip } = getStatusConfig(status);

    return (
        <div {...badgeProps}>
            <span className="group/reviewer-badge flex items-center gap-1.5">
                <UserAvatar user={user} size="xs" tooltip={false} />
                <span className="text-ui-small leading-none">{user.displayName}</span>
                <div className="flex items-center">
                    <Tooltip label={statusTooltip}>
                        <span className="flex shrink-0 items-center justify-center">
                            {statusIcon}
                        </span>
                    </Tooltip>
                    {status === 'changes_requested' ? (
                        <Button
                            kind="blank"
                            size="xsmall"
                            iconOnly
                            leadingIcon={Icon.Refresh}
                            label="Re-request review"
                            rounded
                            onPress={() => {
                                return requestReviewersForChangeRequest({
                                    spaceId: space.id,
                                    changeRequestId: changeRequest.id,
                                    users: [userId],
                                });
                            }}
                        />
                    ) : status === 'requested' ? (
                        <span className="flex w-0 overflow-hidden opacity-0 transition-all duration-200 ease-in-out group-hover/reviewer-badge:w-5 group-hover/reviewer-badge:opacity-100 has-[button[data-loading=true]]:w-5 has-[button[data-loading=true]]:opacity-100">
                            <Button
                                kind="blank"
                                size="xsmall"
                                iconOnly
                                leadingIcon={Icon.X}
                                rounded
                                label="Remove reviewer"
                                onPress={() => {
                                    return apiClient.changeRequestReviewers.removeRequestedReviewerFromChangeRequest(
                                        space.id,
                                        changeRequest.id,
                                        userId
                                    );
                                }}
                            />
                        </span>
                    ) : null}
                </div>
            </span>
        </div>
    );
}

function getStatusConfig(status: ReviewerBadgeData['status']): {
    icon: React.ReactElement;
    tooltip?: string;
} {
    switch (status) {
        case 'approved':
            return {
                icon: <Icon.Check className="text-success-standout" />,
            };
        case 'changes_requested':
            return {
                icon: <Icon.X className="text-danger-standout" />,
            };
        case 'requested':
            return {
                icon: <Icon.Dot className="text-warning-standout" />,
                tooltip: 'Awaiting review',
            };
        default:
            assertNever(status);
    }
}

function AddReviewerPalette(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    pendingRequestedReviewers: ChangeRequestPendingRequestedReviewer[];
    suggestions: ChangeRequestPendingRequestedReviewer[];
    existingReviewers: Set<string>;
}) {
    const { space, changeRequest, pendingRequestedReviewers, suggestions, existingReviewers } =
        props;

    const addReviewerPalette = useAddReviewerPalette({
        space,
        changeRequest,
        suggestions,
        pendingRequestedReviewers,
        existingReviewers,
    });

    const hasReviewers = existingReviewers.size > 0 || pendingRequestedReviewers.length > 0;

    return (
        <PaletteButton
            kind="pill"
            size="small"
            iconOnly={hasReviewers}
            label="Add reviewers"
            leadingIcon={Icon.Plus}
            className="border border-base"
            palette={addReviewerPalette}
        />
    );
}
